import React, { useState } from 'react';
import { Send, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import '../../ContactForm.css';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const INITIAL_FORM = {
  parentName: '',
  athleteName: '',
  athleteAge: '',
  email: '',
  phone: '',
  notes: '',
};

// Express Interest form for the Girls Flag Football program.
// Posts to /api/contact with a Girls-Flag-specific subject so the admin
// inbox can easily filter these leads out of general contact traffic.
const GirlsFlagInterestForm = () => {
  const [formData, setFormData] = useState(INITIAL_FORM);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    const composedMessage = [
      'Program: Girls Flag Football (Ages 12-14)',
      `Athlete Name: ${formData.athleteName}`,
      `Athlete Age: ${formData.athleteAge || 'Not specified'}`,
      '',
      'Notes:',
      formData.notes || '(none)',
    ].join('\n');

    const payload = {
      name: formData.parentName,
      email: formData.email,
      phone: formData.phone,
      subject: 'Girls Flag Football — Express Interest',
      message: composedMessage,
    };

    try {
      const response = await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await response.json();
      if (data.success) {
        setSubmitted(true);
        setFormData(INITIAL_FORM);
      } else {
        setError(data.message || 'Failed to send. Please try again.');
      }
    } catch {
      setError('Network error. Please check your connection and try again.');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="contact-form-success" data-testid="girls-flag-form-success">
        <div className="success-checkmark">✓</div>
        <h3>Interest Received!</h3>
        <p>Thanks! A Panthers coach will reach out within 48 hours with practice and registration details.</p>
        <Button
          className="btn-primary"
          data-testid="girls-flag-form-reset-btn"
          onClick={() => setSubmitted(false)}
        >
          Submit Another
        </Button>
      </div>
    );
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="contact-form"
      data-testid="girls-flag-interest-form"
    >
      {error && <div className="form-error" data-testid="girls-flag-form-error">{error}</div>}

      <div className="form-row">
        <div className="form-group">
          <label htmlFor="gf-parent">Parent / Guardian Name *</label>
          <input
            type="text"
            id="gf-parent"
            name="parentName"
            value={formData.parentName}
            onChange={handleChange}
            required
            placeholder="Your name"
            disabled={loading}
            data-testid="girls-flag-form-parent"
          />
        </div>
        <div className="form-group">
          <label htmlFor="gf-athlete">Athlete Name *</label>
          <input
            type="text"
            id="gf-athlete"
            name="athleteName"
            value={formData.athleteName}
            onChange={handleChange}
            required
            placeholder="Player's name"
            disabled={loading}
            data-testid="girls-flag-form-athlete"
          />
        </div>
      </div>

      <div className="form-row">
        <div className="form-group">
          <label htmlFor="gf-age">Athlete Age *</label>
          <select
            id="gf-age"
            name="athleteAge"
            value={formData.athleteAge}
            onChange={handleChange}
            required
            disabled={loading}
            data-testid="girls-flag-form-age"
          >
            <option value="">Select age...</option>
            <option value="12">12</option>
            <option value="13">13</option>
            <option value="14">14</option>
            <option value="Other">Other (mention in notes)</option>
          </select>
        </div>
        <div className="form-group">
          <label htmlFor="gf-phone">Phone *</label>
          <input
            type="tel"
            id="gf-phone"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            required
            placeholder="(XXX) XXX-XXXX"
            disabled={loading}
            data-testid="girls-flag-form-phone"
          />
        </div>
      </div>

      <div className="form-group">
        <label htmlFor="gf-email">Email *</label>
        <input
          type="email"
          id="gf-email"
          name="email"
          value={formData.email}
          onChange={handleChange}
          required
          placeholder="your.email@example.com"
          disabled={loading}
          data-testid="girls-flag-form-email"
        />
      </div>

      <div className="form-group">
        <label htmlFor="gf-notes">Anything we should know?</label>
        <textarea
          id="gf-notes"
          name="notes"
          value={formData.notes}
          onChange={handleChange}
          placeholder="Prior experience, questions, best time to call..."
          rows={4}
          disabled={loading}
          data-testid="girls-flag-form-notes"
        />
      </div>

      <Button
        type="submit"
        className="btn-primary submit-btn"
        disabled={loading}
        data-testid="girls-flag-form-submit"
      >
        {loading ? (
          <>
            <Loader2 className="animate-spin" size={18} />
            Sending...
          </>
        ) : (
          <>
            <Send size={18} />
            Express Interest
          </>
        )}
      </Button>
    </form>
  );
};

export default GirlsFlagInterestForm;
